/**
 * CAP-40007 TCL-22 / TCL-SECURITY - Role-Based Access Control (RBAC) for the TCL Back Office.
 * Centralized front-end authorization service: the ONLY place that retrieves the current user's
 * roles and takes authorization decisions (menu visibility, route protection, read-only / write,
 * protected content). Components, the guard and the menu MUST depend on this service and must never
 * perform role checks on their own.
 *
 * TCL-SECURITY multi-role model:
 *   ADMIN is exclusive and always wins. For non-admin users, visible and writable areas are combined
 *   across all recognised roles. IDP role strings are matched DIRECTLY against IdpRoles constants \u2013
 *   there is no internal-role mapping layer.
 *
 * Current examples:
 *   MULTIVIN + EXTRACTION -> Saisie (read-only) AND Extraction menus visible.
 *   MULTIVIN + OPERATOR   -> Saisie writable through Operator.
 *   ADMIN + anything      -> ADMIN permissions only.
 */
import { Injectable } from '@angular/core';
import { OidcAuthService } from './oidc-auth.service';
import {
  IdpRoles,
  TclArea,
  RolePolicy,
  RolePolicyEntry,
  PROTECTED_TOPIC_NUMBERS,
  ROLE_PRIORITY
} from './roles.constants';

@Injectable({
  providedIn: 'root'
})
export class AuthorizationService {

  private lastLoggedRoleDecision: string;

  constructor(private oidc: OidcAuthService) {}

  /**
   * TCL-SECURITY - Return the list of IDP role strings the current user holds that are
   * recognised by this application (i.e. present in IdpRoles).
   * The JWT "roles" claim is read from the access token and filtered to known roles.
   * Unrecognised IDP roles are silently dropped so they cannot grant unexpected permissions.
   */
  getEffectiveRoles(): string[] {
    const tokenRoles = this.oidc.getRoles() || [];
    const knownRoles = Object.values(IdpRoles);
    const effective = tokenRoles.filter(r => knownRoles.indexOf(r) !== -1);
  
    return effective;
  }

  /**
  * TCL-SECURITY - Returns the highest-priority recognised role for display and diagnostics.
   * When the user holds multiple roles the highest-priority one is returned (see ROLE_PRIORITY).
  * Permission checks aggregate applicable policies separately.
   */
  getCurrentRole(): string | null {
    const roles = this.getEffectiveRoles();
    if (roles.length === 0) {
      return null;
    }
    const primary = ROLE_PRIORITY.find(p => roles.indexOf(p) !== -1) || roles[0];
   
    return primary;
  }

  // TCL-SECURITY - Whether the current user holds this specific IDP role.
  hasRole(role: string): boolean {
    return this.getEffectiveRoles().indexOf(role) !== -1;
  }

  /**
   * TCL-SECURITY - Whether any applicable role grants access to the given area.
   * ADMIN is exclusive; non-admin roles contribute their visible areas.
   */
  canAccessArea(area: TclArea): boolean {
    return this.getApplicablePolicies().some(policy => policy.areas.indexOf(area) !== -1);
  }

  /**
   * TCL-SECURITY - Whether any applicable role grants write access to the given area.
   * ADMIN is exclusive; otherwise write access wins over read-only access for an area.
   */
  canWrite(area: TclArea): boolean {
    return this.getApplicablePolicies().some(policy => policy.writableAreas.indexOf(area) !== -1);
  }

  // TCL-SECURITY - Convenience: is the current user read-only in the given area?
  isReadOnly(area: TclArea): boolean {
    return !this.canWrite(area);
  }

  /**
   * TCL-SECURITY - Whether the current user can modify protected text/content
   * (e.g. headers, 1.2.1 Performance text). Only ADMIN has this permission.
   * Only ADMIN grants this permission, even when other roles are also present.
   */
  canModifyProtectedContent(): boolean {
    return this.getApplicablePolicies().some(policy => policy.canModifyProtectedContent);
  }

  private getApplicablePolicies(): RolePolicyEntry[] {
    const roles = this.getEffectiveRoles();
    const hasAdmin = roles.indexOf(IdpRoles.ADMIN) !== -1;
    const approvedRoles = hasAdmin ? [IdpRoles.ADMIN] : roles;
    const policies = approvedRoles
      .map(role => RolePolicy[role])
      .filter(policy => !!policy);

    const decisionKey = approvedRoles.join('|');
    if (decisionKey !== this.lastLoggedRoleDecision) {
      this.lastLoggedRoleDecision = decisionKey;
      const areas = this.uniqueAreas(policies, 'areas');
      const writableAreas = this.uniqueAreas(policies, 'writableAreas');
      console.log(JSON.stringify({
        event: 'tcl.rbac.approved-role',
        message: 'Role approved by TCL Back Office authorization logic',
        assignedRoles: roles,
        approvedRoles,
        primaryRole: ROLE_PRIORITY.find(role => roles.indexOf(role) !== -1) || null,
        reason: hasAdmin
          ? 'ADMIN takes precedence over all other roles'
          : 'Non-admin roles combined by area',
        areas,
        writableAreas,
        canModifyProtectedContent: policies.some(policy => policy.canModifyProtectedContent)
      }));
    }

    return policies;
  }

  private uniqueAreas(
    policies: RolePolicyEntry[],
    property: 'areas' | 'writableAreas'
  ): TclArea[] {
    return policies.reduce((areas: TclArea[], policy: RolePolicyEntry) => {
      policy[property].forEach(area => {
        if (areas.indexOf(area) === -1) {
          areas.push(area);
        }
      });
      return areas;
    }, []);
  }

  // CAP-40007 TCL-22 - Whether a given topic number is protected content (e.g. "1.2.1 Performance").
  isProtectedTopic(topicNumber: string): boolean {
    return !!topicNumber && PROTECTED_TOPIC_NUMBERS.indexOf(topicNumber.trim()) !== -1;
  }

  // CAP-40007 TCL-22 - Whether the current user may modify the given topic's content.
  canModifyTopic(topicNumber: string): boolean {
    if (!this.canWrite(TclArea.SAISIE)) {
      return false;
    }
    if (this.isProtectedTopic(topicNumber)) {
      return this.canModifyProtectedContent();
    }
    return true;
  }
}

